import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Ein Fehler ist aufgetreten');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 bg-[hsl(var(--surface-2))]">
      <div className="container mx-auto px-4 max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="card-shadow">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Passwort vergessen</CardTitle>
              <CardDescription>
                Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-[hsl(var(--chart-2)/0.1)] rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-[hsl(var(--chart-2))]" />
                  </div>
                  <div>
                    <p className="font-medium text-[hsl(var(--foreground))]">
                      E-Mail gesendet!
                    </p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                      Falls ein Konto mit <strong>{email}</strong> existiert, erhalten Sie in Kürze einen Link zum Zurücksetzen Ihres Passworts.
                    </p>
                  </div>
                  <Link to="/auth">
                    <Button variant="secondary" className="w-full mt-4" data-testid="back-to-login-button">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Zurück zum Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reset-email">E-Mail-Adresse</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="reset-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        placeholder="ihre@email.ch"
                        required
                        data-testid="forgot-password-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="forgot-password-submit-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    Link senden
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/auth"
                      className="text-sm text-[hsl(var(--primary))] hover:underline"
                      data-testid="forgot-password-back-link"
                    >
                      <ArrowLeft className="w-3 h-3 inline mr-1" />
                      Zurück zum Login
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
